import { FormControl, Grid, MenuItem, Select } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FixedSizeList as List } from 'react-window';
import { useController, useForm } from 'react-hook-form';
import ErrorIcon from '@mui/icons-material/Error';
import { IDistrictViewModel } from 'viewModels';
import { useTheme } from 'styled-components';
import { ReactComponent as ExpandArrowIcon } from '../../assets/icons/iconExpandArrow.svg';
import { Container } from './styles';
import { Input } from '../Inputs/Input';
import { useDistricts } from '../../context/districts';
import { useCourts } from '../../context/courts';
import { useApp } from '../../context';
import { slugify } from '../../utils/slugify';

interface DropDownListProps {
  placeholderTitle: string;
  ufCode: number;
  disabled?: boolean;
  errorText?: string;
  name: string;
  control?: any;
  selectedDistricts?: IDistrictViewModel[];
  onDistrictChange?: (districtName: IDistrictViewModel) => void;
}

export function DropListDistricts({
  placeholderTitle,
  ufCode,
  disabled = false,
  errorText,
  name,
  control,
  onDistrictChange,
  selectedDistricts,
}: DropDownListProps) {
  const { setLoading } = useApp();
  const [isOpen, setIsOpen] = useState(false);
  const { handleFetchDistrictsByUfCode } = useDistricts();
  const { setDistrictSelectedId } = useCourts();
  const theme = useTheme();
  const [filteredDistricts, setFilteredDistricts] = useState<IDistrictViewModel[]>([]);
  const MenuProps = {
    PaperProps: {
      style: {
        backgroundColor: theme.colors.background,
        color: theme.colors.secondary,
      },
    },
    disableScrollLock: true,
  };

  const handleOpen = () => {
    setIsOpen(true);
    document.body.classList.add('no-scroll');
  };
  const [defaultValues] = useState({
    search: '',
  });
  const { control: controlForm } = useForm({
    reValidateMode: 'onChange',
    defaultValues,
  });
  const {
    field: { value, ref, onChange },
  } = useController({
    name,
    control: control ?? controlForm,
  });

  const handleClose = () => {
    setIsOpen(false);
    document.body.classList.remove('no-scroll');
  };

  const handleSelectDistrict = (district: IDistrictViewModel) => {
    onChange(district.name);
    onDistrictChange?.(district);
    setDistrictSelectedId(district.id);
    setIsOpen(false);
  };

  const handleSearchClick = (event: React.MouseEvent<HTMLInputElement>) => {
    if (isOpen) {
      event.stopPropagation();
    }
  };

  const handleGetFilteredDistricts = useCallback(async () => {
    if (ufCode) {
      setLoading(true);
      const filtered = await handleFetchDistrictsByUfCode(ufCode).finally(() => setLoading(false));
      setFilteredDistricts(filtered);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ufCode]);

  useEffect(() => {
    handleGetFilteredDistricts();
  }, [handleGetFilteredDistricts]);

  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) setIsOpen(false);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isOpen]);

  const onRender = ({ index, style }: any) => {
    const district = filteredDistricts[index];
    return (
      <MenuItem
        value={district.name}
        key={district.name}
        style={style}
        selected={selectedDistricts?.some((d) => d.id === district.id)}
        onClick={() => handleSelectDistrict(district)}>
        {district.name}
      </MenuItem>
    );
  };
  return (
    <Container>
      <FormControl fullWidth>
        <Select
          name="district"
          open={isOpen}
          onOpen={handleOpen}
          ref={ref}
          onClose={handleClose}
          classes={{ select: 'select-style' }}
          displayEmpty
          disabled={disabled}
          IconComponent={ExpandArrowIcon}
          MenuProps={MenuProps}
          renderValue={() => value || placeholderTitle}>
          <MenuItem disabled value="">
            {placeholderTitle}
          </MenuItem>
          <Grid display="flex" alignItems="center" justifyContent="center" justifyItems="center">
            <Grid width="90%" padding="1rem 0.5rem">
              <Input
                control={controlForm}
                name="search"
                placeholder="Pesquisar..."
                onClick={handleSearchClick}
                onChange={(e) => {
                  if (e.target.value !== '') {
                    setFilteredDistricts((prev) =>
                      prev.filter((district) =>
                        slugify(district.name).includes(slugify(e.target.value)),
                      ),
                    );
                  } else {
                    handleGetFilteredDistricts();
                  }
                }}
              />
            </Grid>
          </Grid>
          {filteredDistricts.length > 0 ? (
            <List
              width="100%"
              height={350}
              itemCount={filteredDistricts.sort((a, b) => a.name.localeCompare(b.name)).length}
              itemSize={40}>
              {onRender}
            </List>
          ) : (
            <MenuItem disabled>Nenhum distrito encontrado</MenuItem>
          )}
        </Select>
      </FormControl>
      {errorText && (
        <span className="fieldError">
          <ErrorIcon fontSize="inherit" /> {errorText}
        </span>
      )}
    </Container>
  );
}
